import { parsePagingQuery, toQueryParam } from "../../../../handlers/filter";
import { FilterTypes, publisherAllowListMediaTypeToService, PublisherAllowListStatus, publisherAllowListStatusToService, ServicePublisherAllowListMediaType } from "../../../../types";
import { PublisherAllowListArchivedStatus } from "../../types";
import { sortToQueryParam } from "./sort";
/**
 * Generates a query parameter string for filtering publisher groups by their status.
 *
 * @param incomingStatuses - An array of `PublisherAllowListStatus` values or `undefined`.
 *                           Represents the statuses to filter by, excluding archived statuses.
 * @param archiveStatus - A `PublisherAllowListArchivedStatus` value or `undefined`.
 *                        Specifies whether to include archived statuses, non-archived statuses, or both.
 * @returns A query parameter string for filtering by status, or an empty string if no filtering is required.
 *
 * The function processes the provided `incomingStatuses` and `archiveStatus` to determine the appropriate
 * statuses to include in the query. If no statuses are provided or the filtering criteria are not met,
 * an empty string is returned. Otherwise, the function constructs a query parameter string using the
 * `toQueryParam` utility.
 */
const statusParamValue = (incomingStatuses, archiveStatus) => {
    if (!incomingStatuses?.length &&
        (!archiveStatus?.length || archiveStatus === PublisherAllowListArchivedStatus.BOTH)) {
        return "";
    }
    const nonArchivedStatuses = (incomingStatuses && !Array.isArray(incomingStatuses)
        ? [incomingStatuses]
        : incomingStatuses || []).filter((status) => status && status !== PublisherAllowListStatus.ARCHIVED);
    const statuses = [];
    if (archiveStatus === PublisherAllowListArchivedStatus.ARCHIVED) {
        statuses.push(PublisherAllowListStatus.ARCHIVED);
    }
    else {
        statuses.push(...(nonArchivedStatuses?.length ? nonArchivedStatuses : [PublisherAllowListStatus.READY]));
        if (archiveStatus === PublisherAllowListArchivedStatus.BOTH &&
            !statuses.includes(PublisherAllowListStatus.ARCHIVED)) {
            statuses.push(PublisherAllowListStatus.ARCHIVED);
        }
    }
    if (statuses.length) {
        return toQueryParam({
            field: "status",
            type: FilterTypes.IN,
            value: statuses.map(publisherAllowListStatusToService)
        });
    }
    return "";
};
/**
 * Converts a set of "where" filters into a query string for use in HTTP requests.
 *
 * @param wheres - An array of filter objects representing the "where" conditions
 *                 for filtering publisher groups. Each filter includes a field,
 *                 value, and type.
 *
 * @returns A query string containing the serialized filter parameters.
 *
 * @remarks
 * - Ensures that certain default filters (`mediaType` and `status`) are always present.
 * - Handles specific fields (`search`, `status`, `mediaType`, `id`) with custom logic
 *   for converting their values into query parameters.
 * - Filters out invalid or empty values during the conversion process.
 * - Supports array values for certain fields and applies appropriate serialization.
 *
 * @example
 * ```typescript
 * const filters = [
 *   { field: "id", value: [1, 2, 3], type: FilterTypes.IN },
 *   { field: "status", value: ["active"], type: FilterTypes.EQ }
 * ];
 * const queryString = whereToQueryParams(filters);
 * console.log(queryString); // Output: "&ids=1,2,3&statuses=active"
 * ```
 */
const whereToQueryParams = (wheres) => {
    let params = "";
    const whereMap = new Map(wheres?.map((filter) => [filter.field, filter]) || []);
    // ensuring the following filters are present while looping through map.
    if (!whereMap.has("id") && !whereMap.has("mediaType")) {
        whereMap.set("mediaType", { value: [], type: FilterTypes.IN, field: "mediaType" });
    }
    if (!whereMap.has("status")) {
        whereMap.set("status", { value: [], type: FilterTypes.IN, field: "status" });
    }
    if (wheres) {
        whereMap.forEach((filter, field) => {
            const valueArr = Array.isArray(filter.value) ? filter.value : [filter.value];
            switch (field) {
                case "search": {
                    const value = toQueryParam(filter);
                    if (value) {
                        params = `${params}&search=${value}`;
                    }
                    break;
                }
                case "status": {
                    const archiveStatus = whereMap.get("archivedStatus")?.value;
                    const value = statusParamValue(valueArr.filter((status) => !!status), Array.isArray(archiveStatus) ? archiveStatus[0] : archiveStatus);
                    if (value) {
                        params = `${params}&statuses=${value}`;
                    }
                    break;
                }
                case "mediaType": {
                    const value = valueArr
                        .map((val) => publisherAllowListMediaTypeToService(val))
                        .filter((v) => v !== undefined && v !== ServicePublisherAllowListMediaType.NONE);
                    if (value?.length) {
                        params = `${params}&mediaType=${toQueryParam({
                            ...filter,
                            value
                        })}`;
                    }
                    break;
                }
                case "id": {
                    const value = valueArr.filter((id) => !!id);
                    if (value.length) {
                        params = `${params}&ids=${toQueryParam({
                            ...filter,
                            value
                        }, { array: true })}`;
                    }
                    break;
                }
            }
            return params;
        });
    }
    return params;
};
/**
 * Converts the provided filters and sorting options into a query string.
 *
 * @param filters - An object containing the filtering criteria for publisher groups.
 *                  Includes `where` conditions and `paging` information.
 * @param sort - An optional sorting configuration specifying the fields and order
 *               for sorting the publisher groups.
 * @returns A query string starting with "?" if parameters are present, or an empty
 *          string if no parameters are provided.
 */
export const toQueryParams = (filters, sort) => {
    const params = `${whereToQueryParams(filters.where)}${parsePagingQuery(filters.paging)}${sortToQueryParam(sort)}`;
    return params.length ? `?${params}` : "";
};
//# sourceMappingURL=params.js.map